// Decision 65 (Block J): changing a repeating task from one of its occurrences.
//
// A repeating task is one row, the series; the occurrences on screen are drawn from it
// (utils/recurrenceUtils.js). Editing an occurrence used to save the form straight onto the series,
// which restarted the series at that occurrence: every earlier occurrence vanished. Now the edit asks
// which occurrences to change and sends that to PUT /api/tasks/:id/series, which works out the
// writes (Aidey Backend/lib/taskSeries.js):
//   this       only this occurrence: an exception stored against the series (Carla's decision 2)
//   following  this occurrence and every one after it (a new series from here)
//   all        every occurrence; a new time or day moves them all by the same amount
//
// Carla's decision 2 (2026-10-12) also: ticking one occurrence done, or moving it on the board,
// changes THAT occurrence only (setOccurrenceState). It used to PUT { completed } onto the series,
// which completed every occurrence and made the server write a stray copy of the series. Deleting
// one occurrence skips it (skipOccurrence) instead of deleting the series.
import { api } from '../api/client';
import { messageFromError } from './apiError';
import { showErrorToast, showToast } from './toast';

export const SERIES_SCOPES = [
  { value: 'this', label: 'Only this occurrence' },
  { value: 'following', label: 'This and following occurrences' },
  { value: 'all', label: 'All occurrences' },
];

// Deleting from an occurrence: that one, or the whole task.
export const DELETE_SCOPES = [
  { value: 'this', label: 'Only this occurrence' },
  { value: 'all', label: 'Every occurrence' },
];

// A task time as the app stores it: a wall clock with no zone ("2026-10-19T09:00:00").
export function toWallClock(value) {
  if (value === null || value === undefined || value === '') return value;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

// The occurrence being edited, as the edit form keeps it. Named by its own start in the series
// (occurrenceStart), never by where an override shows it.
export function occurrenceOf(task) {
  if (!task?.isRecurringInstance || task.parentTaskId === undefined || task.parentTaskId === null) return null;
  return { seriesId: task.parentTaskId, start: task.occurrenceStart || toWallClock(task.datetime), index: task.occurrenceIndex, rule: task.recurrence || null };
}

// Two repeat rules that say the same thing. The form rebuilds the rule in its own shape (an
// interval of 1 added, an end date re-written as an instant), so the stored rule and the form's are
// compared by meaning: "only this occurrence" cannot change the rule, and must not be refused for a
// rule that was never changed.
function ruleMeaning(r) {
  if (!r) return null;
  const until = r.until ? new Date(r.until).getTime() : null;
  return JSON.stringify({
    type: String(r.type || r.frequency || r.rule || '').toLowerCase(),
    interval: Number(r.interval) > 0 ? Number(r.interval) : 1,
    day: r.day ? String(r.day).toLowerCase() : null,
    date: r.date !== undefined && r.date !== null ? Number(r.date) : null,
    unit: r.unit || null,
    count: Number(r.count) > 0 ? Number(r.count) : null,
    until: Number.isFinite(until) ? until : null,
  });
}

// The request body for PUT /api/tasks/:id/series.
export function seriesEditRequest({ occurrence, scope, fields }) {
  const updates = { ...fields };
  if ('datetime' in updates) updates.datetime = toWallClock(updates.datetime);
  if ('deadline' in updates) updates.deadline = toWallClock(updates.deadline);
  delete updates.date;
  delete updates.time;
  // Only this one: an unchanged rule is not sent (the server refuses any rule change here, by its
  // exact shape); a changed one is, so the server says why it cannot be done for one occurrence.
  if (scope === 'this' && 'recurrence' in updates && occurrence.rule && ruleMeaning(updates.recurrence) === ruleMeaning(occurrence.rule)) {
    delete updates.recurrence;
  }
  const body = { scope, occurrenceStart: toWallClock(occurrence.start), updates };
  if (Number.isInteger(occurrence.index)) body.occurrenceIndex = occurrence.index;
  return body;
}

// Block M2: a change to one occurrence is pushed to the calendars the series is synced to (Google,
// Outlook), and the server answers what each said: calendarSync [{ provider, label, ok, error? }].
// The change is saved in Aidey either way. A calendar that did not take it is said, by name and
// with its reason, in place of the plain success (which the calendar would contradict). null when
// every calendar took it, or none was asked.
export function calendarSyncProblem(calendarSync) {
  const refused = (Array.isArray(calendarSync) ? calendarSync : []).filter((c) => c && c.ok === false);
  if (!refused.length) return null;
  const parts = refused.map((c) => `${c.label || 'Your calendar'} did not take the change${c.error ? `: ${String(c.error).replace(/\.?\s*$/, '.')}` : '.'}`);
  return `Saved in Aidey, but ${parts.join(' ')}`;
}
const CALENDAR_PROBLEM_MS = 12000;   // long enough to read a reason
const sayCalendarProblem = (data) => {
  const problem = calendarSyncProblem(data && data.calendarSync);
  if (problem) showErrorToast(problem, { ms: CALENDAR_PROBLEM_MS });
  return !!problem;
};

const REPEAT_OFF_SAVED = 'This occurrence is now a single task. Earlier occurrences stay in your history.';
const SAVED = {
  this: 'Only this occurrence was changed.',
  following: 'Changed from this occurrence on.',
  all: 'Every occurrence was changed.',
};

// Send it; on success tell the app to reload its tasks (the server decides the rows). true when the
// server confirmed the change, false otherwise; a failure is said with the server's own reason.
// UI-24 item 25 is released per account: the server says whether it is on for this account
// (currentUser.features, Aidey Backend lib/featureFlags). Only an exact true is on.
export function repeatOffKeepsOccurrence(currentUser) {
  return currentUser?.features?.repeatOffKeepsOccurrence === true;
}

export async function saveSeriesEdit({ seriesId, occurrence, scope, fields, repeatOffKeeps = false, source }) {
  try {
    // source: which path this is, for the due-date history (the server reads anything else as 'api').
    const body = { ...seriesEditRequest({ occurrence, scope, fields }), ...(source ? { dueChange: { source } } : {}) };
    const res = await api(`/api/tasks/${seriesId}/series`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!data?.task) throw new Error('The server did not confirm the change. Refresh to check.');
    window.dispatchEvent(new Event('aidey:tasksUpdated'));
    // UI-24 item 25: Repeat off (this and following, or all) leaves the occurrence as one task.
    const stopped = repeatOffKeeps === true && scope !== 'this' && fields && 'recurrence' in fields && !fields.recurrence;
    if (!sayCalendarProblem(data)) showToast(stopped ? REPEAT_OFF_SAVED : (SAVED[scope] || SAVED.following), { type: 'success' });
    return true;
  } catch (err) {
    showErrorToast(messageFromError(err, 'The repeating task was not changed.'));
    return false;
  }
}

// Tick one occurrence done (or not), or move it on the board: { completed, inProgress }. Resolves
// the series row the server sent back, or null (the server's reason already shown), so the caller
// can put back what it showed.
export async function setOccurrenceState({ seriesId, occurrence, updates }) {
  try {
    const body = { scope: 'this', occurrenceStart: toWallClock(occurrence.start), updates };
    if (Number.isInteger(occurrence.index)) body.occurrenceIndex = occurrence.index;
    const res = await api(`/api/tasks/${seriesId}/series`, { method: 'PUT', body: JSON.stringify(body) });
    const data = await res.json();
    if (!data?.task) throw new Error('The server did not confirm the change. Refresh to check.');
    sayCalendarProblem(data);
    return data.task;
  } catch (err) {
    showErrorToast(messageFromError(err, 'That change was not saved.'));
    return null;
  }
}

// Delete one occurrence: the server skips it. Resolves the series row, or null as above.
export async function skipOccurrence({ seriesId, occurrence }) {
  try {
    const start = encodeURIComponent(toWallClock(occurrence.start));
    const res = await api(`/api/tasks/${seriesId}/occurrence?start=${start}`, { method: 'DELETE' });
    const data = await res.json();
    if (!data?.task) throw new Error('The server did not confirm the delete. Refresh to check.');
    // Said here, not by the caller: toasts stack in one place, so a success shown after a
    // calendar's refusal would sit on top of it (Block M2 browser check).
    if (!sayCalendarProblem(data)) showToast('That occurrence was deleted. The others are unchanged.', { type: 'success' });
    return data.task;
  } catch (err) {
    showErrorToast(messageFromError(err, 'That occurrence was not deleted.'));
    return null;
  }
}

// What the app shows at once, before the server answers: the same entry the server will write
// (Aidey Backend/lib/taskSeries.js), on the series row in the app's list. The server's row
// replaces it when it answers; a refusal puts the old row back.
export function applyOccurrenceLocally(series, occurrenceStart, { completed, inProgress, skip } = {}) {
  const name = toWallClock(occurrenceStart);
  const list = Array.isArray(series.recurrenceExceptions) ? series.recurrenceExceptions : [];
  const rest = list.filter((e) => toWallClock(e.occurrenceStart) !== name);
  let entry = { ...(list.find((e) => toWallClock(e.occurrenceStart) === name) || {}), occurrenceStart: name };
  if (skip) entry = { occurrenceStart: name, skip: true };
  const override = { ...(entry.override || {}) };
  if (completed === true) { entry.completed = true; delete override.inProgress; }
  if (completed === false) { delete entry.completed; delete entry.completedBy; delete entry.completedAt; }
  if (inProgress === true && completed !== true) override.inProgress = true;
  if (inProgress === false) delete override.inProgress;
  if (Object.keys(override).length) entry.override = override; else delete entry.override;
  const keep = entry.skip || entry.completed || entry.override;
  return { ...series, recurrenceExceptions: keep ? [...rest, entry] : rest };
}
